import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Toast } from 'antd-mobile';
import { useI18n } from '../../../hooks/useI18n';
import { getMySupervisorScenarios, SupervisorScenarioInfo } from '@/lib/api/ai-supervisor';
import ManyDotLoading from '@/components/Loaders/ManyDotLoading';
import PCMaxWidthContainer from '@/components/Layout/PCMaxWidthContainer';
import shouyefengmian from '@/assets/images/dudao/shouyefengmian.png';
import zhuangshi1 from '@/assets/images/dudao/zhuangshi1.png';
import xiangyou from '@/assets/images/dudao/xiangyou.png';
import xiaopie from '@/assets/images/dudao/xiaopie.png';


type TabType = 'all' | 'participated' | 'notParticipated';

const AiSupervisorList: React.FC = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [scenarioList, setScenarioList] = useState<SupervisorScenarioInfo[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [searchText, setSearchText] = useState<string>('');

  // 获取我的督导场景列表
  const fetchScenarioList = async () => {
    setLoading(true);
    try {
      const response = await getMySupervisorScenarios({
        page: 1,
        pageSize: 100
      });

      if (response.success && response.data) {
        setScenarioList(response.data.list || []);
      } else {
        Toast.show({ content: '获取督导场景列表失败', icon: 'fail' });
      }
    } catch (error) {
      console.error('获取督导场景列表失败:', error);
      Toast.show({ content: '获取督导场景列表失败', icon: 'fail' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchScenarioList();
  }, []);

  // 进入场景详情
  const handleEnterScenario = (scenarioId: string) => {
    navigate(`/ai-supervisor/detail/${scenarioId}`);
  };

  // 过滤场景列表
  const filteredList = scenarioList.filter(scenario => {
    // 搜索过滤
    if (searchText && !scenario.name.includes(searchText)) {
      return false;
    }
    // Tab过滤 - 根据练习次数筛选
    if (activeTab === 'participated') {
      return (scenario.practice_count || 0) > 0;
    } else if (activeTab === 'notParticipated') {
      return (scenario.practice_count || 0) === 0;
    }
    return true;
  });

  return (
    <PCMaxWidthContainer bgGradient='bg-[#f5f7fa]'>
      {/* 顶部区域 */}
      <div className='relative h-[175px] overflow-hidden'>
        {/* 背景图片 - 包含所有装饰元素 */}
        <img
          src={shouyefengmian}
          alt="AI督导"
          className='absolute inset-0 w-full h-full object-cover'
        />
          {/* 标题文字 */}
          <div className='absolute left-[32px] top-[29px]'>
          <div className='text-[#0a1b39] text-[30px] font-medium leading-[1.6] mb-[3px]'>
            AI督导
          </div>
          <div className='text-[#86909c] text-[16px] leading-[1.6]'>
            AI Supervisor
          </div>
        </div>

        {/* 搜索框 */}
        <div className='absolute left-[15px] right-[15px] top-[126px]'>
          <div className='bg-white rounded-[12px] h-[40px] px-[16px] flex items-center gap-[8px]'>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M6.5 11.5C9.26142 11.5 11.5 9.26142 11.5 6.5C11.5 3.73858 9.26142 1.5 6.5 1.5C3.73858 1.5 1.5 3.73858 1.5 6.5C1.5 9.26142 3.73858 11.5 6.5 11.5Z" stroke="#86909C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12.5 12.5L10.5 10.5" stroke="#86909C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <input
              type="text"
              placeholder='搜索'
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className='flex-1 text-[14px] text-[#000000] outline-none bg-transparent'
            />
          </div>
        </div>
      </div>

      {/* Tab标签页 */}
      <div className='relative px-4 mt-[11px] mb-4'>
        <div className='flex items-center gap-6'>
          <button
            onClick={() => setActiveTab('all')}
            className={`pb-2 text-base transition-colors relative ${
              activeTab === 'all'
                ? 'text-[#485066] font-medium'
                : 'text-[#86909c] font-normal'
            }`}
          >
            全部
            {activeTab === 'all' && (
              <img src={xiaopie} alt="" className='absolute bottom-[-10px] left-1/2 -translate-x-1/2 h-[40px] w-auto' />
            )}
          </button>
          <button
            onClick={() => setActiveTab('participated')}
            className={`pb-2 text-sm transition-colors relative ${
              activeTab === 'participated'
                ? 'text-[#485066] font-medium'
                : 'text-[#86909c] font-normal'
            }`}
          >
            已参与
            {activeTab === 'participated' && (
              <img src={xiaopie} alt="" className='absolute bottom-[-10px] left-1/2 -translate-x-1/2 h-[40px] w-auto' />
            )}
          </button>
          <button
            onClick={() => setActiveTab('notParticipated')}
            className={`pb-2 text-sm transition-colors relative ${
              activeTab === 'notParticipated'
                ? 'text-[#485066] font-medium'
                : 'text-[#86909c] font-normal'
            }`}
          >
            未参与
            {activeTab === 'notParticipated' && (
              <img src={xiaopie} alt="" className='absolute bottom-[-10px] left-1/2 -translate-x-1/2 h-[40px] w-auto' />
            )}
          </button>
        </div>
      </div>

      {/* 场景列表 */}
      <div className='px-[15px] pb-6 pb-[100px]'>
        {loading ? (
          <div className='flex items-center justify-center py-20'>
            <ManyDotLoading />
          </div>
        ) : filteredList.length === 0 ? (
          <div className='flex flex-col items-center justify-center py-20'>
            <div className='text-[#909399] text-sm'>暂无督导场景</div>
          </div>
        ) : (
          <div className='grid grid-cols-1 md:grid-cols-2 gap-[15px]'>
            {filteredList.map((scenario, index) => (
              <motion.div
                key={scenario.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                onClick={() => handleEnterScenario(scenario.id)}
                className='bg-[rgba(255,255,255,0.9)] rounded-[16px] h-[85px] px-4 py-[21px] cursor-pointer active:scale-98 transition-transform relative shadow-[0_4px_16px_rgba(0,0,0,0.02)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.11)]'
              >
                <div className='flex items-start justify-between'>
                  <div className='flex items-start gap-[17px] flex-1'>
                    {/* 图标 */}
                    <div className='w-[13px] h-[12.235px] flex-shrink-0 mt-[5px]'>
                      <img
                        src={zhuangshi1}
                        alt=""
                        className='w-full h-full'
                      />
                    </div>

                    {/* 场景信息 */}
                    <div className='flex-1 min-w-0'>
                      <div className='text-[#0a1b39] text-[14px] leading-[1.6] font-normal mb-[9px]'>
                        {scenario.name}
                      </div>
                      <div className='text-[#86909c] text-[12px] leading-[1.6] line-clamp-1'>
                        {scenario.description || '针对你昨天的卡顿问题生成的情话训练'}
                      </div>
                    </div>
                  </div>

                  {/* 右箭头 */}
                  <div className='w-5 h-5 flex-shrink-0 ml-2'>

                    <img
                        src={xiangyou}
                        alt=""
                        className='w-[20px]'
                      />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </PCMaxWidthContainer>
  );
};

export default AiSupervisorList;
